import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { rootUrl } from '../../../config/config';
import { useAuth } from '../AuthContext';
import DriverTransactions from './driverTrans/DriverTransactions';
import DriverTimeline from './timeline/DriverTimeline';
import AddDriverTimelineNote from './timeline/AddDriverTimelineNote';
import DriverLicenseVerificationsList from './license/DriverLicenseVerificationsList';
import DriverAllocationsList from './allocations/DriverAllocationsList';
import EntityDocs from '../documents/EntityDocs';
import DriverNextOfKin from './nextOfKin/DriverNextOfKin';
import { primaryContact, relationshipLabel } from './nextOfKin/nextOfKinHelpers';
import PhoneActions from './nextOfKin/PhoneActions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faUserSlash, faArrowLeft, faHistory, faCheckCircle, faCar, faList, faUserPlus, faFolderOpen, faUsers } from '@fortawesome/free-solid-svg-icons';
import BlacklistModal from './BlacklistModal';
import CreatePortalAccountModal from '../../common/CreatePortalAccountModal';

const formatDate = (iso) => {
    if (!iso) return 'N/A';
    try {
        const d = new Date(iso);
        if (isNaN(d)) return iso;
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (e) {
        return iso;
    }
};

export default function DriverDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [driver, setDriver] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('transactions');
    const [showAddNote, setShowAddNote] = useState(false);
    const [perms, setPerms] = useState([]);
    const [blacklistModalOpen, setBlacklistModalOpen] = useState(false);
    const [blacklistAction, setBlacklistAction] = useState('blacklist');
    const [blacklistModalError, setBlacklistModalError] = useState('');
    const [portalModalOpen, setPortalModalOpen] = useState(false);
    const [nokContacts, setNokContacts] = useState(null);
    const token = localStorage.getItem('authToken') || '';

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!id) return;
        setLoading(true);
        setError(null);

        fetch(`${rootUrl}/drivers/id_no/${id}?include_users=true`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
        })
            .then((res) => {
                if (res.status === 404) {
                    return null;
                }
                if (!res.ok) throw new Error(`Failed to fetch driver (${res.status})`);
                return res.json();
            })
            .then((data) => {
                setDriver(data || null);
            })
            .catch((err) => setError(err.message || 'Failed to load'))
            .finally(() => setLoading(false));
    }, [id]);

    // Primary next of kin shown in the header; the Next of Kin tab keeps it current via onContactsChange
    useEffect(() => {
        if (!driver?.id) return;
        fetch(`${rootUrl}/drivers/${driver.id}/next-of-kin`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.json())
            .then(j => setNokContacts(j.success ? j.data || [] : null))
            .catch(() => setNokContacts(null));
    }, [driver?.id, token]);

    const handleNokChange = useCallback((contacts) => setNokContacts(contacts), []);
    const primaryNok = primaryContact(nokContacts || []);

    useEffect(() => {
        const t = localStorage.getItem('authToken') || '';
        if (!t) return;
        fetch(`${rootUrl}/api/users/me/permissions`, { headers: { Authorization: `Bearer ${t}` } })
            .then(r => r.json())
            .then(j => setPerms(j.data || []))
            .catch(() => setPerms([]));
    }, []);

    const canBlacklist = () => perms.includes('drivers.blacklist') || perms.includes('roles.manage');
    const canManageUsers = () => perms.includes('users.manage') || perms.includes('roles.manage');

    const isBlacklisted = (drv) => drv?.is_blacklisted === true;

    const openBlacklist = (action) => {
        setBlacklistAction(action);
        setBlacklistModalError('');
        setBlacklistModalOpen(true);
    };

    const handleBlacklistConfirm = async ({ reason }) => {
        const t = localStorage.getItem('authToken') || '';
        const url = `${rootUrl}/drivers/${driver.id}/${blacklistAction === 'blacklist' ? 'blacklist' : 'unblacklist'}`;
        try {
            const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` }, body: JSON.stringify({ reason }) });
            if (!res.ok) {
                let msg = 'Action failed';
                try {
                    const j = await res.json();
                    msg = j.message || j.error || JSON.stringify(j);
                } catch (e) {
                    try { msg = await res.text(); } catch (e2) { /* ignore */ }
                }
                setBlacklistModalError(msg || 'Action failed');
                return;
            }
            // refresh driver
            const refreshed = await fetch(`${rootUrl}/drivers/id_no/${driver.id_no}?include_users=true`, { headers: { Authorization: `Bearer ${t}` } });
            const json = await refreshed.json();
            setDriver(json);
            setBlacklistModalOpen(false);
        } catch (err) {
            setBlacklistModalError(err.message || 'Blacklist action failed');
        }
    };

    // fetch tabbed data when tab changes
    // no longer fetching driver payment types here

    // derived display values
    const balance = driver ? parseFloat(driver.bal || 0) : 0;
    const absBalance = Math.abs(isNaN(balance) ? 0 : balance);
    // Color / background mapping by absolute thresholds:
    // 0 - 3000 => green
    // 3001 - 6000 => orange
    // 6001 - 10000 => red
    // >10000 => dark red
    let balanceClass = 'text-gray-500';
    let balanceBg = 'bg-gray-50';

    if (balance > 0) {
        if (absBalance <= 3000) {
            balanceClass = 'text-green-600';
            balanceBg = 'bg-green-50';
        } else if (absBalance <= 6000) {
            balanceClass = 'text-orange-600';
            balanceBg = 'bg-orange-50';
        } else if (absBalance <= 10000) {
            balanceClass = 'text-red-600';
            balanceBg = 'bg-red-50';
        } else {
            balanceClass = 'text-red-800';
            balanceBg = 'bg-red-100';
        }
    } else if (balance < 0) {
        // Negative balances: use red hues, scale with magnitude
        if (absBalance <= 3000) {
            balanceClass = 'text-red-600';
            balanceBg = 'bg-red-50';
        } else if (absBalance <= 6000) {
            balanceClass = 'text-red-700';
            balanceBg = 'bg-red-100';
        } else if (absBalance <= 10000) {
            balanceClass = 'text-red-800';
            balanceBg = 'bg-red-200';
        } else {
            balanceClass = 'text-red-900';
            balanceBg = 'bg-red-300';
        }
    }

    // assigned plate - allow a couple of common fallbacks
    const assignedPlate = driver ? (driver.assigned_plate || driver.plate || driver.assigned_plate_no) : null;
    return (
        <div className="w-full max-w-full mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
                <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Driver Details</h1>
                <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                <p className="text-lg text-[#2a2e30] opacity-80">Profile for {driver?.name || id}</p>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-3">
                <h2 className="text-2xl font-semibold">Driver Details</h2>
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                    <button onClick={() => navigate('/drivers')} className="w-full sm:w-auto px-3 py-2 bg-gray-100 rounded hover:bg-gray-200 transition-colors text-center text-sm">Back to list</button>
                    <button onClick={() => navigate(-1)} className="w-full sm:w-auto px-3 py-2 bg-gray-100 rounded hover:bg-gray-200 transition-colors text-center text-sm">Go Back</button>
                </div>
            </div>

            {loading && <div className="text-sm text-gray-600">Loading...</div>}
            {error && <div className="text-sm text-red-600">{error}</div>}

            {!loading && !error && !driver && (
                <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl shadow-xl p-6">
                    <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                        <FontAwesomeIcon icon={faUserSlash} className="text-4xl text-gray-400" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">Driver Not Found</h3>
                    <p className="text-gray-500 mb-8 text-center max-w-md">
                        The driver you are looking for could not be found. They may have been deleted or the ID is incorrect.
                    </p>
                    <button
                        onClick={() => navigate('/drivers')}
                        className="px-6 py-3 bg-[#FF5733] text-white rounded-lg hover:bg-[#E64A19] transition-colors flex items-center gap-2 font-semibold shadow-lg shadow-orange-500/20"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                        Back to Drivers List
                    </button>
                </div>
            )}

            {!loading && driver && (
                <div>
                    <div className="bg-white rounded-2xl shadow-xl p-6 mb-4">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                            <div className="flex items-start gap-4">
                                <div className="w-16 h-16 bg-[#fff8f5] rounded-full flex items-center justify-center text-2xl font-bold text-[#FF5733]">{(driver.name || 'D').charAt(0)}</div>
                                <div>
                                    <div className="text-sm text-gray-500">Name</div>
                                    <div className="font-semibold text-2xl text-[#2a2e30]">{driver.name || '—'}</div>
                                    <div className="mt-2 text-sm text-gray-600">{driver.phone || '—'}</div>
                                    {nokContacts && (
                                        <div className="mt-2 text-sm">
                                            {primaryNok ? (
                                                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-gray-600">
                                                    <span className="text-gray-500">Next of kin:</span>
                                                    <button onClick={() => setActiveTab('nextOfKin')} className="font-medium text-[#2a2e30] hover:text-[#FF5733]">
                                                        {primaryNok.name}
                                                    </button>
                                                    <span className="text-gray-400">({relationshipLabel(primaryNok.relationship)})</span>
                                                    <PhoneActions phone={primaryNok.phone} label={primaryNok.name} compact />
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => setActiveTab('nextOfKin')}
                                                    className="px-3 py-1.5 text-xs font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
                                                >
                                                    Add next of kin (required for allocation)
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex flex-col items-start md:items-end space-y-2">
                                <div className="flex items-center gap-2">
                                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${driver.license_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{driver.license_verification_status || (driver.license_verified ? 'Verified' : 'Unverified')}</span>
                                    <span className="px-3 py-1 rounded-full text-sm font-semibold bg-gray-100 text-gray-700">{driver.status || '—'}</span>
                                    {driver.is_blacklisted && (
                                        <span className="px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-700">BLACKLISTED</span>
                                    )}
                                </div>

                                <div className="text-sm text-gray-500">Balance</div>
                                <div className={`font-mono text-2xl font-semibold ${balanceClass} ${balanceBg} px-3 py-1 rounded-none inline-block`}>
                                    {balance < 0 ? '-KES ' : 'KES '}{Math.abs(balance).toLocaleString()}
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            <div>
                                <div className="text-sm text-gray-500">Driver ID</div>
                                <div className="font-medium text-[#2a2e30]">{driver.id ?? '—'}</div>
                            </div>

                            <div>
                                <div className="text-sm text-gray-500">National ID</div>
                                <div className="font-medium text-[#2a2e30]">{driver.id_no || '—'}</div>
                            </div>

                            <div>
                                <div className="text-sm text-gray-500">License No</div>
                                <div className="font-medium text-[#2a2e30]">{driver.license_no || '—'}</div>
                            </div>



                            <div>
                                <div className="text-sm text-gray-500">License Expiry</div>
                                <div className="font-medium text-[#2a2e30]">{formatDate(driver.license_expiry)}</div>
                            </div>

                            <div>
                                <div className="text-sm text-gray-500">Referred By</div>
                                <div className="font-medium text-[#2a2e30]">{driver.referred_by_obj?.name || driver.referred_by || '—'}</div>
                            </div>

                            <div>
                                <div className="text-sm text-gray-500">Assigned Date</div>
                                <div className="font-medium text-[#2a2e30]">{formatDate(driver.assigned_date)}</div>
                            </div>

                            <div>
                                <div className="text-sm text-gray-500">Assigned By</div>
                                <div className="font-medium text-[#2a2e30]">{driver.assigned_by_obj?.name || driver.assigned_by || '—'}</div>
                            </div>

                            <div>
                                <div className="text-sm text-gray-500">Assigned Plate</div>
                                <div className="font-medium text-[#2a2e30]">{assignedPlate ? (
                                    <Link to={`/cars/${encodeURIComponent(assignedPlate)}`} className="text-[#FF5733] hover:underline" title={`View car ${assignedPlate}`}>{assignedPlate}</Link>
                                ) : '—'}</div>
                            </div>

                            <div>
                                <div className="text-sm text-gray-500">Drive Folder</div>
                                {driver.drive_link ? (
                                    <a href={driver.drive_link} target="_blank" rel="noreferrer" className="text-sm text-[#FF5733] hover:underline break-words">Open Drive</a>
                                ) : (
                                    <div className="text-sm text-gray-500">—</div>
                                )}
                            </div>

                            <div>
                                <div className="text-sm text-gray-500">License Sticker</div>
                                {driver.license_sticker_link ? (
                                    <a href={driver.license_sticker_link} target="_blank" rel="noreferrer" className="text-sm text-[#FF5733] hover:underline break-words">View Sticker</a>
                                ) : (
                                    <div className="text-sm text-gray-500">—</div>
                                )}
                            </div>

                            <div>
                                <div className="text-sm text-gray-500">License Verified On</div>
                                <div className="font-medium text-[#2a2e30]">{formatDate(driver.license_verified_on)}</div>
                            </div>

                            <div>
                                <div className="text-sm text-gray-500">License Verified By</div>
                                <div className="font-medium text-[#2a2e30]">{driver.license_verified_by_obj?.name || driver.license_verified_by || '—'}</div>
                            </div>

                            <div>
                                <div className="text-sm text-gray-500">Input By</div>
                                <div className="font-medium text-[#2a2e30]">{driver.input_by_obj?.name || driver.input_by || '—'}</div>
                            </div>

                            <div>
                                <div className="text-sm text-gray-500">Input Date</div>
                                <div className="font-medium text-[#2a2e30]">{formatDate(driver.input_date)}</div>
                            </div>

                            <div>
                                <div className="text-sm text-gray-500">Last Edited</div>
                                <div className="font-medium text-[#2a2e30]">{formatDate(driver.edit_date || driver.updated_at)}</div>
                            </div>

                            <div>
                                <div className="text-sm text-gray-500">Edited By</div>
                                <div className="font-medium text-[#2a2e30]">{driver.edit_by_obj?.name || driver.edit_by || driver.updated_by_obj?.name || driver.updated_by || '—'}</div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-4 bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">Portal Access</p>
                            {driver.portal_user_id ? (
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-0.5">
                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                        Has Account
                                    </span>
                                    <span className="text-sm text-gray-600">@{driver.portal_username}</span>
                                    {driver.portal_first_login_at ? (
                                        <span className="text-xs text-green-600 font-medium">Activated</span>
                                    ) : (
                                        <span className="text-xs text-amber-500 font-medium">Never logged in</span>
                                    )}
                                </div>
                            ) : (
                                <span className="text-sm text-gray-400">No portal account</span>
                            )}
                        </div>
                        {!driver.portal_user_id && canManageUsers() && (
                            <button
                                onClick={() => setPortalModalOpen(true)}
                                className="inline-flex items-center gap-2 bg-orange-500 text-white rounded-xl px-4 py-2 text-sm font-semibold hover:bg-orange-600 transition"
                            >
                                <FontAwesomeIcon icon={faUserPlus} />
                                Create Account
                            </button>
                        )}
                    </div>

                    {canBlacklist() && (
                        <div className="mt-4 flex gap-2">
                            {!isBlacklisted(driver) ? (
                                <button onClick={() => openBlacklist('blacklist')} className="px-4 py-2 bg-red-600 text-white rounded">Blacklist Driver</button>
                            ) : (
                                <button onClick={() => openBlacklist('unblacklist')} className="px-4 py-2 bg-green-600 text-white rounded">Unblacklist Driver</button>
                            )}
                        </div>
                    )}

                    <CreatePortalAccountModal
                        isOpen={portalModalOpen}
                        onClose={() => setPortalModalOpen(false)}
                        onSuccess={(result) => {
                            setDriver(prev => prev ? { ...prev, portal_user_id: result.userId, portal_username: result.username, portal_first_login_at: null } : prev);
                            setPortalModalOpen(false);
                        }}
                        actorType="driver"
                        entityId={driver.id}
                        prefill={{ name: driver.name, email: driver.email }}
                        token={token}
                    />

                    <div className="bg-white rounded-2xl shadow-xl p-6">
                        {/* Tab Headers */}
                        <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
                            <button
                                onClick={() => setActiveTab('transactions')}
                                className={`px-4 sm:px-6 py-3 font-semibold text-sm sm:text-base transition-all duration-200 border-b-2 whitespace-nowrap ${activeTab === 'transactions'
                                    ? 'border-[#FF5733] text-[#FF5733]'
                                    : 'border-transparent text-gray-600 hover:text-[#2a2e30]'
                                    }`}
                            >
                                <span className="md:hidden"><FontAwesomeIcon icon={faList} /></span>
                                <span className="hidden md:inline">Transactions</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('timeline')}
                                className={`px-4 sm:px-6 py-3 font-semibold text-sm sm:text-base transition-all duration-200 border-b-2 whitespace-nowrap ${activeTab === 'timeline'
                                    ? 'border-[#FF5733] text-[#FF5733]'
                                    : 'border-transparent text-gray-600 hover:text-[#2a2e30]'
                                    }`}
                            >
                                <span className="md:hidden"><FontAwesomeIcon icon={faHistory} /></span>
                                <span className="hidden md:inline">Timeline</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('license')}
                                className={`px-4 sm:px-6 py-3 font-semibold text-sm sm:text-base transition-all duration-200 border-b-2 whitespace-nowrap ${activeTab === 'license'
                                    ? 'border-[#FF5733] text-[#FF5733]'
                                    : 'border-transparent text-gray-600 hover:text-[#2a2e30]'
                                    }`}
                            >
                                <span className="md:hidden"><FontAwesomeIcon icon={faCheckCircle} /></span>
                                <span className="hidden md:inline">License Verifications</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('allocations')}
                                className={`px-4 sm:px-6 py-3 font-semibold text-sm sm:text-base transition-all duration-200 border-b-2 whitespace-nowrap ${activeTab === 'allocations'
                                    ? 'border-[#FF5733] text-[#FF5733]'
                                    : 'border-transparent text-gray-600 hover:text-[#2a2e30]'
                                    }`}
                            >
                                <span className="md:hidden"><FontAwesomeIcon icon={faCar} /></span>
                                <span className="hidden md:inline">Vehicle Assignments</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('nextOfKin')}
                                className={`px-4 sm:px-6 py-3 font-semibold text-sm sm:text-base transition-all duration-200 border-b-2 whitespace-nowrap ${activeTab === 'nextOfKin'
                                    ? 'border-[#FF5733] text-[#FF5733]'
                                    : 'border-transparent text-gray-600 hover:text-[#2a2e30]'
                                    }`}
                            >
                                <span className="md:hidden"><FontAwesomeIcon icon={faUsers} /></span>
                                <span className="hidden md:inline">Next of Kin</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('docs')}
                                className={`px-4 sm:px-6 py-3 font-semibold text-sm sm:text-base transition-all duration-200 border-b-2 whitespace-nowrap ${activeTab === 'docs'
                                    ? 'border-[#FF5733] text-[#FF5733]'
                                    : 'border-transparent text-gray-600 hover:text-[#2a2e30]'
                                    }`}
                            >
                                <span className="md:hidden"><FontAwesomeIcon icon={faFolderOpen} /></span>
                                <span className="hidden md:inline">Docs</span>
                            </button>
                        </div>

                        {/* Timeline Tab Content */}
                        {activeTab === 'timeline' && (
                            <div>
                                <div className="flex justify-end mb-4">
                                    <button
                                        onClick={() => setShowAddNote(true)}
                                        className="w-10 h-10 md:w-auto md:h-auto p-0 md:px-4 md:py-2 bg-[#FF5733] text-white rounded-full md:rounded-lg hover:bg-[#E64A19] transition-colors flex items-center justify-center gap-2 shadow-md md:shadow-none"
                                    >
                                        <FontAwesomeIcon icon={faPlus} />
                                        <span className="hidden md:inline">Add Note</span>
                                    </button>
                                </div>
                                <DriverTimeline driverId={driver.id} />
                            </div>
                        )}

                        {/* License Verifications Tab Content */}
                        {activeTab === 'license' && (
                            <div>
                                <DriverLicenseVerificationsList driverId={driver.id} driver={driver} user={user} />
                            </div>
                        )}

                        {/* Allocations Tab Content */}
                        {activeTab === 'allocations' && (
                            <div>
                                <DriverAllocationsList driverId={driver.id_no} />
                            </div>
                        )}

                        {/* Transactions Tab Content - replaced with DriverTransactions page */}
                        {activeTab === 'transactions' && (
                            <div>
                                <DriverTransactions driverId={driver.id} token={token} />
                            </div>
                        )}

                        {/* Next of Kin Tab Content */}
                        {activeTab === 'nextOfKin' && (
                            <div>
                                <DriverNextOfKin driver={driver} onContactsChange={handleNokChange} />
                            </div>
                        )}

                        {/* Docs Tab Content */}
                        {activeTab === 'docs' && (
                            <div>
                                <EntityDocs entityType="driver" entityKey={driver.id} />
                            </div>
                        )}
                    </div>
                </div>
            )}

            <AddDriverTimelineNote
                open={showAddNote}
                onClose={() => setShowAddNote(false)}
                driverId={driver?.id}
                onSaved={() => {
                    // Force refresh of timeline by toggling active tab or using a key
                    // For now, just close, the user can refresh or we can implement a refresh trigger
                    // A simple way is to reset the timeline component key if we had one, 
                    // but since DriverTimeline fetches on mount/prop change, we might need to trigger it.
                    // For simplicity, we can just let the user see the new note if they refresh or switch tabs.
                    // Or better, pass a refresh trigger to DriverTimeline.
                    // But for now, let's just close.
                    setShowAddNote(false);
                    // Ideally we should trigger a re-fetch in DriverTimeline
                    // We can do this by passing a key to DriverTimeline that changes
                    setActiveTab('transactions');
                    setTimeout(() => setActiveTab('timeline'), 10);
                }}
            />
            <BlacklistModal
                open={blacklistModalOpen}
                onClose={() => { setBlacklistModalOpen(false); setBlacklistModalError(''); }}
                onConfirm={handleBlacklistConfirm}
                driver={driver}
                action={blacklistAction}
                error={blacklistModalError}
            />
        </div>
    );
}
